// src/views/Tarjetas/FiltroEESS.jsx
import React, { useEffect, useRef, useState } from "react";
import useEscClose from "../../useEscClose";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";
import TarjetasAutocomplete from "../../../components/autocomplete/TarjetasAutocomplete";


const DEBOUNCE_MS = 300;
const MIN_CHARS = 2;

// tamaños (ajustables)
const BACKEND_PAGE_SIZE = 200; // cuántos traemos por página al precargar TODO
const LOCAL_CHUNK = 60; // cuántos mostramos por “página” en el dropdown local

// ✅ al borrar el último chip (y sin EE.SS.) auto-restaura la lista
const AUTO_SUBMIT_WHEN_EMPTY = true;

export default function FiltroEESS({
  initialFilters = {},
  onSubmit = () => {},
  onClose = () => {},
}) {
  const panelRef = useRef(null);

  // ====== TARJETAS (igual que antes) ======
  const [tarQuery, setTarQuery] = useState("");
  const [tarOpen, setTarOpen] = useState(false);
  const [tarOpciones, setTarOpciones] = useState([]);
  const [tarLoading, setTarLoading] = useState(false);
  const [tarHasMore, setTarHasMore] = useState(false);
  const [tarPage, setTarPage] = useState(0);
  const tarDebRef = useRef(null);
  const tarBlurTimer = useRef(null);
  const tarListRef = useRef(null);
  const tarLatestReq = useRef({ q: "", page: 0 });
  const [tarChips, setTarChips] = useState([]); // [{id, numero}]

  // ====== EE.SS. (cache local + filtro local) ======
  const [eessId, setEessId] = useState(initialFilters.eessId || "");
  const [eessNombre, setEessNombre] = useState(initialFilters.eessNombre || "");

  const [eQuery, setEQuery] = useState("");
  const [eOpen, setEOpen] = useState(false);

  // cache de TODAS las EESS
  const [eCacheAll, setECacheAll] = useState([]); // [{id,codigo,nombre,label}]
  const [eCacheLoading, setECacheLoading] = useState(false);
  const [eCacheLoadedAll, setECacheLoadedAll] = useState(false);

  // listado mostrado (resultado del filtro local + paginado local)
  const [eOpciones, setEOpciones] = useState([]);
  const [eLocalShown, setELocalShown] = useState(LOCAL_CHUNK); // cuántos mostramos del filtro local

  // debounce para el filtro local
  const eDebRef = useRef(null);

  // kickoff para precargar al montar el filtro (y evitar doble en StrictMode)
  const eKickoffRef = useRef(false);

  useEscClose(() => {
    onClose();
    document.activeElement?.blur();
  }, true);

  // ====== Rehidratación (solo una vez) ======
  const hasHydratedRef = useRef(false);
  useEffect(() => {
    if (hasHydratedRef.current) return;

    const chips = [];
    if (
      Array.isArray(initialFilters.tarjetaIds) ||
      Array.isArray(initialFilters.tarjetaNumero)
    ) {
      const ids = Array.isArray(initialFilters.tarjetaIds)
        ? initialFilters.tarjetaIds
        : [];
      const numeros = Array.isArray(initialFilters.tarjetaNumero)
        ? initialFilters.tarjetaNumero
        : [];
      const len = Math.max(ids.length, numeros.length);
      for (let i = 0; i < len; i++) {
        const id = ids[i] != null ? String(ids[i]) : null;
        const numero = numeros[i] != null ? String(numeros[i]) : "";
        if (id || numero) chips.push({ id, numero });
      }
    } else {
      const id =
        initialFilters.tarjetaId != null
          ? String(initialFilters.tarjetaId)
          : null;
      const numero = initialFilters.tarjeta
        ? String(initialFilters.tarjeta)
        : "";
      if (id || numero) chips.push({ id, numero });
    }
    setTarChips(chips);

    setEessId(initialFilters.eessId || "");
    setEessNombre(initialFilters.eessNombre || "");

    hasHydratedRef.current = true;
  }, [initialFilters]);

  // ====== FETCH tarjetas (igual) ======
  const fetchTarjetas = async (q, page = 0, size = 20) => {
    const qq = (q || "").trim();
    if (qq.length < MIN_CHARS) {
      if (page === 0) {
        setTarOpciones([]);
        setTarHasMore(false);
      }
      return;
    }
    try {
      setTarLoading(true);
      tarLatestReq.current = { q: qq, page };
      const url = apiRoutes.eessHabilitadas.autocomplete(
        "tarjeta",
        qq,
        page,
        size,
      );
      const json = await apiFetchJson(url);
      if (tarLatestReq.current.q !== qq || tarLatestReq.current.page !== page)
        return;

      const items = Array.isArray(json?.items) ? json.items : [];
      const mapped = items
        .map((it) => ({
          id: it.id != null ? String(it.id) : null,
          numero: String(it.numero || ""),
          label: it.label || it.numero,
        }))
        .filter((x) => x.numero);

      setTarOpciones(page === 0 ? mapped : (prev) => [...prev, ...mapped]);
      setTarHasMore(Boolean(json?.hasMore) || mapped.length === size);
    } catch {
      if (page === 0) {
        setTarOpciones([]);
        setTarHasMore(false);
      }
    } finally {
      setTarLoading(false);
    }
  };

  // ====== PRECARGA: traer TODAS las EESS por páginas (q="") ======
  const fetchAllEessPage = async (page, size = BACKEND_PAGE_SIZE) => {
    const url = apiRoutes.eessHabilitadas.autocomplete("eess", "", page, size);
    const json = await apiFetchJson(url);
    const items = Array.isArray(json?.items) ? json.items : [];
    const mapped = items.map((it) => ({
      id: String(it.id), // EESS_SQ_COD
      codigo: it.codigo, // EESS_CD_ESTACION
      nombre: it.nombre, // EESS_NM_ESTACION
      label: it.label || `${it.codigo || ""} - ${it.nombre || ""}`.trim(),
    }));
    const hasMore = Boolean(json?.hasMore) || mapped.length === size;
    return { mapped, hasMore };
  };

  const ensureEessCache = async () => {
    if (eCacheLoading || eCacheLoadedAll) return;
    try {
      setECacheLoading(true);
      let page = 0;
      let all = [];
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { mapped, hasMore } = await fetchAllEessPage(
          page,
          BACKEND_PAGE_SIZE,
        );
        all = all.concat(mapped);
        setECacheAll(all); // ir actualizando para que ya se vea algo
        page += 1;
        if (!hasMore) break;
      }
      setECacheAll(all);
      setECacheLoadedAll(true);
      // primera vista: sin filtro → mostramos primeras LOCAL_CHUNK
      setELocalShown(LOCAL_CHUNK);
      setEOpciones(all.slice(0, LOCAL_CHUNK));
    } catch (e) {
      console.error("Error precargando EESS:", e);
      setECacheAll([]);
      setEOpciones([]);
    } finally {
      setECacheLoading(false);
    }
  };

  // precargar TODAS las estaciones apenas se abre el filtro (monta el componente)
  useEffect(() => {
    if (eKickoffRef.current) return;
    eKickoffRef.current = true;
    ensureEessCache();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ====== Filtro local sobre la cache (con debounce) ======
  const applyLocalFilter = (q) => {
    const qq = (q || "").trim().toLowerCase();
    const base = eCacheAll;
    let filtered = base;
    if (qq.length >= 1) {
      filtered = base.filter((it) => {
        return (
          (it.codigo && String(it.codigo).toLowerCase().includes(qq)) ||
          (it.nombre && String(it.nombre).toLowerCase().includes(qq))
        );
      });
    }
    setELocalShown(LOCAL_CHUNK);
    setEOpciones(filtered.slice(0, LOCAL_CHUNK));
  };

  useEffect(() => {
    clearTimeout(eDebRef.current);
    eDebRef.current = setTimeout(() => {
      // filtro local (no llama a backend)
      applyLocalFilter(eQuery);
    }, DEBOUNCE_MS);
    return () => clearTimeout(eDebRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eQuery, eCacheAll]);

  // ====== Scroll infinito LOCAL (solo muestra más del array filtrado) ======
  const eListRef = useRef(null);
  const handleEScroll = () => {
    const el = eListRef.current;
    if (!el) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    if (!nearBottom) return;

    const qq = (eQuery || "").trim().toLowerCase();
    let filtered = eCacheAll;
    if (qq.length >= 1) {
      filtered = eCacheAll.filter(
        (it) =>
          (it.codigo && String(it.codigo).toLowerCase().includes(qq)) ||
          (it.nombre && String(it.nombre).toLowerCase().includes(qq)),
      );
    }

    if (eLocalShown >= filtered.length) return;

    const nextShown = Math.min(filtered.length, eLocalShown + LOCAL_CHUNK);
    setELocalShown(nextShown);
    setEOpciones(filtered.slice(0, nextShown));
  };

  // ====== Tarjetas: debounce y scroll infinito (igual) ======
  useEffect(() => {
    clearTimeout(tarDebRef.current);
    if (tarQuery.trim().length >= MIN_CHARS) {
      tarDebRef.current = setTimeout(() => {
        setTarPage(0);
        fetchTarjetas(tarQuery, 0, 20);
        setTarOpen(true);
      }, DEBOUNCE_MS);
    } else {
      setTarOpciones([]);
      setTarOpen(false);
    }
    return () => clearTimeout(tarDebRef.current);
  }, [tarQuery]);

  const handleTarScroll = (e) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    if (nearBottom && tarHasMore && !tarLoading) {
      const next = tarPage + 1;
      setTarPage(next);
      fetchTarjetas(tarQuery, next, 20);
    }
  };

  // ====== pickers ======
  const addTarjetaChip = (op) => {
    const id = op?.id ?? null;
    const numero = op?.numero || "";
    if (!numero) return;
    setTarChips((prev) => {
      const exists = prev.some(
        (c) => (c.numero && c.numero === numero) || (c.id && c.id === id),
      );
      if (exists) return prev;
      return [...prev, { id, numero }];
    });
    setTarQuery("");
    setTarOpen(false);
    setTarOpciones([]);
  };

  const removeTarjetaChip = (chip) => {
    setTarChips((prev) => {
      const next = prev.filter(
        (c) => !(c.numero === chip.numero && c.id === chip.id),
      );
      // ⬇️ actualizar lista principal si se quedaron sin chips y no hay EESS,
      //    pero NO cerrar el panel de filtros
      if (
        AUTO_SUBMIT_WHEN_EMPTY &&
        next.length === 0 &&
        !eessId &&
        !eessNombre
      ) {
        onSubmit({
          tarjetaIds: [],
          tarjetaNumero: [],
          eessId: eessId || "",
          eessNombre: eessNombre || "",
        });
        // (sin onClose)
      }
      return next;
    });
  };

  const pickEess = (op) => {
    setEessId(op?.id || "");
    setEessNombre(op?.nombre || "");
    setEQuery("");
    setEOpen(false);
    // dejamos cache para siguientes usos
  };

  // helpers blur
  const closeTarSoon = () => {
    clearTimeout(tarBlurTimer.current);
    tarBlurTimer.current = setTimeout(() => setTarOpen(false), 120);
  };
  const closeEessSoon = () => {
    setTimeout(() => setEOpen(false), 120);
  };

  // ====== submit (botón Aplicar) ======
  const handleSubmit = (e) => {
    e.preventDefault();
    const tarjetaIds = tarChips.map((c) => c.id).filter((x) => x != null);
    const tarjetaNumero = tarChips.map((c) => c.numero).filter(Boolean);

    onSubmit({
      tarjetaIds,
      tarjetaNumero,
      eessId: eessId || "",
      eessNombre: eessNombre || "",
    });
    onClose(); // ← solo se cierra al aplicar manualmente
  };

  // al borrar el chip de EE.SS.: recargar lista principal pero NO cerrar el filtro
  const removeEessChipAndSubmit = () => {
    setEessId("");
    setEessNombre("");
    const tarjetaIds = tarChips.map((c) => c.id).filter((x) => x != null);
    const tarjetaNumero = tarChips.map((c) => c.numero).filter(Boolean);
    onSubmit({
      tarjetaIds,
      tarjetaNumero,
      eessId: "",
      eessNombre: "",
    });
    // (sin onClose)
  };

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div
        ref={panelRef}
        className="drawer-panel overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="drawer-header">
          <h2 className="text-title">Filtros</h2>
          <button onClick={onClose} className="btn-cancel" title="Cerrar">
            &times;
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="drawer-body space-y-4">
          {/* Nro. de Tarjeta */}
<div>
  <label className="form-label-fil">Nro. de Tarjeta</label>

  <TarjetasAutocomplete
    tipo="numero"
    placeholder="Buscar tarjeta..."
    value={null} // usamos chips, no selección simple
    onChange={(tarjeta) => {
      if (!tarjeta) return;

      const id = tarjeta.id != null ? String(tarjeta.id) : null;
      const numero = String(tarjeta.numero || "");

      if (!numero) return;

      setTarChips((prev) => {
        const exists = prev.some(
          (c) =>
            (c.numero && c.numero === numero) ||
            (c.id && id && c.id === id),
        );
        if (exists) return prev;
        return [...prev, { id, numero }];
      });
    }}
  />

  {/* Chips */}
  <div className="flex flex-wrap mt-1 gap-1">
    {tarChips.map((c) => (
      <span
        key={`${c.id ?? "noid"}-${c.numero}`}
        className="bg px-2 py-1 rounded-full text-sm flex items-center gap-1"
        title={c.id ? `ID: ${c.id}` : ""}
      >
        {c.numero}
        <button type="button" onClick={() => removeTarjetaChip(c)}>
          &times;
        </button>
      </span>
    ))}
  </div>
</div>


          {/* EE.SS. (cache total + filtro local) */}
          <div className="relative">
            <label className="form-label-fil" htmlFor="flt-eess">
              EE.SS.
            </label>
            <input
              id="flt-eess"
              className="input"
              placeholder="Código o nombre de EE.SS."
              value={eQuery}
              onChange={(e) => setEQuery(e.target.value)}
              onFocus={() => {
                setEOpen(true);
                ensureEessCache(); // empieza a precargar TODO si aún no está
                if (eCacheAll.length && eOpciones.length === 0) {
                  setELocalShown(LOCAL_CHUNK);
                  setEOpciones(eCacheAll.slice(0, LOCAL_CHUNK));
                }
              }}
              onBlur={closeEessSoon}
              autoComplete="off"
            />

            {(eessId || eessNombre) && (
              <div className="flex flex-wrap mt-1 gap-1">
                <span className="bg px-2 py-1 rounded-full text-sm flex items-center gap-1">
                  {eessNombre || eessId}
                  <button type="button" onClick={removeEessChipAndSubmit}>
                    &times;
                  </button>
                </span>
              </div>
            )}

            {eOpen && (
              <div className="absolute z-10 mt-1 w-full border rounded bg-white shadow-lg">
                <div
                  ref={eListRef}
                  className="max-h-64 overflow-auto"
                  onScroll={handleEScroll}
                >
                  {/* Estados de carga de la cache */}
                  {eCacheLoading && eCacheAll.length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-500">
                      Cargando estaciones…
                    </div>
                  )}

                  {/* Lista (filtro local) */}
                  {eOpciones.map((op) => {
                    const active = String(eessId) === String(op.id);
                    return (
                      <div
                        key={op.id}
                        className={`px-3 py-2 cursor-pointer hover:bg-gray-50 ${active ? "bg-gray-50" : ""}`}
                        onMouseDown={() => pickEess(op)}
                        title={op.label}
                      >
                        <span className="text-gray-500 mr-2">{op.codigo}</span>
                        <span className="truncate">{op.nombre}</span>
                      </div>
                    );
                  })}

                  {/* Mensajes vacíos */}
                  {!eCacheLoading &&
                    eCacheAll.length > 0 &&
                    eOpciones.length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        Sin coincidencias
                      </div>
                    )}
                </div>

                <div className="p-2 border-t bg-gray-50 flex items-center justify-between">
                  <button
                    type="button"
                    className="text-sm text-green-700 hover:underline"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setEOpen(false)}
                  >
                    Cerrar
                  </button>
                  {(eQuery || eessId) && (
                    <button
                      type="button"
                      className="text-sm text-green-700 hover:underline"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setEQuery("");
                        setELocalShown(LOCAL_CHUNK);
                        setEOpciones(eCacheAll.slice(0, LOCAL_CHUNK));
                      }}
                    >
                      Limpiar
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="text-right">
            <button type="submit" className="btn">
              Aplicar Filtros
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
